/**
 * Example usage with themes:
 *
 * <Grid
 *   focusedColumn={1}
 *   hiddenColumns={hiddenColumnsForProps(props)}
 *   relativeWidths={[1, 2, 1]}
 * >
 *   <ThemeProvider theme={DarkBrownTheme}>
 *     <ViewStack>
 *       <ChatsList {...props} />
 *     </ViewStack>
 *   </ThemeProvider>
 *   <ThemeProvider theme={legacyTheme}>
 *     <ViewStack>
 *       <DOMCache cacheSize={3}>
 *         <ChatView {...props} />
 *       </DOMCache>
 *     </ViewStack>
 *   </ThemeProvider>
 *   <ThemeProvider theme={legacyTheme}>
 *     <ViewStack>
 *       <CustomerDetails {...props} />
 *     </ViewStack>
 *   </ThemeProvider>
 * </Grid>
 */
import * as React from "react";

import { ResponsiveBreakpoint } from "constants/responsive-breakpoint";

import ColumnsView from "./ColumnsView";

export enum GridColumnType {
  First,
  Second,
  Third,
}

export enum GridColumnSetup {
  TwoColumns,
  ThreeColumns,
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getLayoutColumnsCount(isMobile: boolean, isTablet: boolean, gridColumnSetup: GridColumnSetup) {
  if (isMobile) {
    return 1;
  }

  if (isTablet || gridColumnSetup === GridColumnSetup.TwoColumns) {
    return 2;
  }

  return 3;
}

interface IGridProps {
  mobileLayoutWidth?: number;
  focusedColumn?: GridColumnType;
  hiddenColumns?: Set<number>;
  minWidths?: number[];
  relativeWidths?: number[];
  overflowHidden?: boolean;
  zIndices?: number[];
  children: React.ReactNode;
  noBorder?: boolean;
}

interface IDefaultProps {
  mobileLayoutWidth: number;
}

interface IGridState {
  mobileLayout: boolean;
}

// FIXME: Fix this the next time the file is edited.
// @ts-expect-error TS(2322): Type 'typeof Grid' is not assignable to type 'Comp... Remove this comment to see the full error message
// eslint-disable-next-line react/display-name
const Grid: React.ComponentClass<IGridProps> = class extends React.PureComponent<
  IGridProps & IDefaultProps,
  IGridState
> {
  static defaultProps: IDefaultProps = {
    mobileLayoutWidth: ResponsiveBreakpoint.Mobile,
  };

  constructor(props: IGridProps & IDefaultProps) {
    super(props);
    this.state = {
      mobileLayout: this.isInMobileLayout(),
    };
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentDidMount() {
    this.onResize();
    window.addEventListener("resize", this.onResize);
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize);
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private onResize = () => {
    this.setState({
      mobileLayout: this.isInMobileLayout(),
    });
  };

  private isInMobileLayout = (): boolean => window.innerWidth < this.props.mobileLayoutWidth;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    const { children, focusedColumn, hiddenColumns, minWidths, relativeWidths, overflowHidden, zIndices, noBorder } =
      this.props;
    const { mobileLayout } = this.state;
    return (
      <ColumnsView
        mobileLayout={mobileLayout}
        focusedColumn={focusedColumn || GridColumnType.First}
        hiddenColumns={hiddenColumns || new Set()}
        minWidths={minWidths}
        relativeWidths={relativeWidths}
        overflowHidden={overflowHidden}
        zIndices={zIndices}
        noBorder={noBorder}
      >
        {children}
      </ColumnsView>
    );
  }
};

export default Grid;
